import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  spotifyApi,
  type AlbumMatch,
  type AlbumMiss,
  type SpotifyPlaylist,
} from "@/api/spotify";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Checkbox } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

export function Playlists() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [globalSyncing, setGlobalSyncing] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Per-row "Sync now" + monitor toggle in-flight markers.
  const [rowSyncing, setRowSyncing] = useState<Set<string>>(new Set());
  const [rowToggling, setRowToggling] = useState<Set<string>>(new Set());

  const authQ = useQuery({
    queryKey: ["spotify", "auth"],
    queryFn: spotifyApi.auth.status,
  });

  const playlistsQ = useQuery({
    queryKey: ["spotify", "playlists"],
    queryFn: spotifyApi.playlists.list,
    enabled: authQ.data?.connected === true,
    // Background refetch so queue progress (matched/unmatched counts) updates.
    refetchInterval: 30_000,
  });

  // ---- Mutations -----------------------------------------------------------

  const setMonitored = async (p: SpotifyPlaylist, next: boolean) => {
    setRowToggling((s) => new Set(s).add(p.id));
    try {
      await spotifyApi.playlists.setMonitored(p.id, next);
      toast(
        next
          ? `Queueing albums from ${p.name}…`
          : `Stopped monitoring ${p.name}`,
        next ? "success" : "info",
      );
      qc.invalidateQueries({ queryKey: ["spotify", "playlists"] });
    } catch (err) {
      toast(
        err instanceof Error ? err.message : "Could not update playlist",
        "error",
      );
    } finally {
      setRowToggling((s) => {
        const next = new Set(s);
        next.delete(p.id);
        return next;
      });
    }
  };

  const syncOne = async (p: SpotifyPlaylist) => {
    setRowSyncing((s) => new Set(s).add(p.id));
    try {
      const summary = await spotifyApi.playlists.sync(p.id);
      const parts: string[] = [];
      if (summary.matched > 0) parts.push(`${summary.matched} new albums queued`);
      if (summary.unmatched > 0) parts.push(`${summary.unmatched} unmatched`);
      if (summary.already_queued > 0)
        parts.push(`${summary.already_queued} already queued`);
      toast(
        parts.length === 0
          ? `${p.name}: nothing new`
          : `${p.name}: ${parts.join(", ")}`,
        summary.unmatched > 0 ? "info" : "success",
      );
      qc.invalidateQueries({ queryKey: ["spotify", "playlists"] });
      // If the row is expanded, refresh its album detail too.
      if (expanded === p.id)
        qc.invalidateQueries({ queryKey: ["spotify", "playlists", p.id, "albums"] });
    } catch (err) {
      toast(err instanceof Error ? err.message : "Sync failed", "error");
    } finally {
      setRowSyncing((s) => {
        const next = new Set(s);
        next.delete(p.id);
        return next;
      });
    }
  };

  const syncAll = async () => {
    setGlobalSyncing(true);
    try {
      const res = await spotifyApi.playlists.syncAll();
      toast(`Synced ${res.synced} playlist${res.synced === 1 ? "" : "s"}`, "success");
      qc.invalidateQueries({ queryKey: ["spotify", "playlists"] });
    } catch (err) {
      toast(err instanceof Error ? err.message : "Sync failed", "error");
    } finally {
      setGlobalSyncing(false);
    }
  };

  const bulkMonitor = async (next: boolean) => {
    const items = playlistsQ.data ?? [];
    const targets = items.filter((p) => p.monitored !== next);
    if (targets.length === 0) {
      toast(
        next ? "All playlists already monitored" : "No playlists are monitored",
        "info",
      );
      return;
    }
    if (
      !confirm(
        next
          ? `Start monitoring ${targets.length} playlist(s)? Albums will start queueing immediately.`
          : `Stop monitoring ${targets.length} playlist(s)?`,
      )
    ) {
      return;
    }
    setBulkBusy(true);
    try {
      // Sequential to keep server load predictable; matches the per-row UX.
      for (const p of targets) {
        await spotifyApi.playlists.setMonitored(p.id, next);
      }
      toast(
        next
          ? `Monitoring ${targets.length} playlist(s)`
          : `Stopped monitoring ${targets.length} playlist(s)`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["spotify", "playlists"] });
    } catch (err) {
      toast(err instanceof Error ? err.message : "Bulk update failed", "error");
      qc.invalidateQueries({ queryKey: ["spotify", "playlists"] });
    } finally {
      setBulkBusy(false);
    }
  };

  // ---- Render --------------------------------------------------------------

  // Auth/connection gating: if Spotify isn't connected, show an empty state
  // pointing the user to settings.
  if (authQ.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Playlists" description="Synced from Spotify." />
        <p className="text-sm text-foreground/60">Loading…</p>
      </div>
    );
  }

  if (!authQ.data?.connected) {
    return (
      <div className="space-y-6">
        <PageHeader title="Playlists" description="Synced from Spotify." />
        <EmptyState
          title="Spotify isn't connected"
          description="Save your Spotify app credentials and authorise the connection to start syncing playlists."
          action={
            <Link to="/settings/spotify">
              <Button>Open Spotify settings</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const playlists = playlistsQ.data ?? [];
  const monitoredCount = playlists.filter((p) => p.monitored).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Playlists"
        description="Spotify playlists. Toggle Monitored to auto-queue every album in a playlist for download."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => bulkMonitor(false)}
              disabled={bulkBusy || monitoredCount === 0}
            >
              Unmonitor all
            </Button>
            <Button
              variant="secondary"
              onClick={() => bulkMonitor(true)}
              disabled={
                bulkBusy ||
                playlists.length === 0 ||
                monitoredCount === playlists.length
              }
            >
              Monitor all
            </Button>
            <Button onClick={syncAll} loading={globalSyncing}>
              Sync now
            </Button>
          </div>
        }
      />

      {playlistsQ.isLoading ? (
        <p className="text-sm text-foreground/60">Loading playlists…</p>
      ) : playlistsQ.isError ? (
        <EmptyState
          title="Couldn't load playlists"
          description={
            playlistsQ.error instanceof Error
              ? playlistsQ.error.message
              : "Backend returned an error."
          }
          action={
            <Button variant="secondary" onClick={() => playlistsQ.refetch()}>
              Retry
            </Button>
          }
        />
      ) : playlists.length === 0 ? (
        <EmptyState
          title="No playlists synced yet"
          description="Click Sync now to pull your playlists from Spotify."
          action={
            <Button onClick={syncAll} loading={globalSyncing}>
              Sync now
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="w-12 px-2 py-2"></th>
                <th className="px-3 py-2 font-medium">Playlist</th>
                <th className="px-3 py-2 font-medium">Owner</th>
                <th className="px-3 py-2 font-medium tabular-nums">Tracks</th>
                <th className="px-3 py-2 font-medium">Monitored</th>
                <th className="px-3 py-2 font-medium">Match status</th>
                <th className="px-3 py-2 font-medium">Last synced</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {playlists.map((p) => {
                const isExpanded = expanded === p.id;
                const toggling = rowToggling.has(p.id);
                const syncing = rowSyncing.has(p.id);
                return (
                  <RowGroup
                    key={p.id}
                    playlist={p}
                    expanded={isExpanded}
                    toggling={toggling}
                    syncing={syncing}
                    onToggleExpand={() =>
                      setExpanded((curr) => (curr === p.id ? null : p.id))
                    }
                    onSetMonitored={(v) => setMonitored(p, v)}
                    onSyncNow={() => syncOne(p)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row + expanded detail
// ---------------------------------------------------------------------------

function RowGroup({
  playlist: p,
  expanded,
  toggling,
  syncing,
  onToggleExpand,
  onSetMonitored,
  onSyncNow,
}: {
  playlist: SpotifyPlaylist;
  expanded: boolean;
  toggling: boolean;
  syncing: boolean;
  onToggleExpand: () => void;
  onSetMonitored: (next: boolean) => void;
  onSyncNow: () => void;
}) {
  const lastSynced = p.last_synced_at
    ? formatRelative(p.last_synced_at)
    : "never";

  return (
    <>
      <tr
        className={cn(
          "border-t border-line transition hover:bg-surface/40",
          expanded && "bg-surface/40",
        )}
      >
        <td className="px-2 py-2">
          {p.image_url ? (
            <img
              src={p.image_url}
              alt=""
              className="h-10 w-10 rounded object-cover"
              loading="lazy"
            />
          ) : (
            <div className="grid h-10 w-10 place-items-center rounded bg-muted text-[10px] text-foreground/40">
              ♪
            </div>
          )}
        </td>
        <td
          className="cursor-pointer px-3 py-2 align-top"
          onClick={onToggleExpand}
        >
          <div className="font-medium">{p.name}</div>
          {p.description && (
            <div className="line-clamp-1 max-w-md text-xs text-foreground/50">
              {p.description}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-foreground/70">{p.owner_name}</td>
        <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
          {p.track_count}
        </td>
        <td className="px-3 py-2">
          <label className="inline-flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={p.monitored}
              disabled={toggling}
              onChange={(e) => onSetMonitored(e.target.checked)}
            />
            <span
              className={cn(
                "text-xs",
                p.monitored ? "text-foreground" : "text-foreground/50",
              )}
            >
              {p.monitored ? "Yes" : "No"}
            </span>
          </label>
        </td>
        <td className="px-3 py-2 text-xs">
          {p.matched_albums === 0 && p.unmatched_albums === 0 ? (
            <span className="text-foreground/40">—</span>
          ) : (
            <span>
              <span className="text-green-300">{p.matched_albums} queued</span>
              {p.unmatched_albums > 0 && (
                <>
                  {" · "}
                  <span className="text-amber-300">
                    {p.unmatched_albums} unmatched
                  </span>
                </>
              )}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-xs text-foreground/60">{lastSynced}</td>
        <td className="space-x-2 px-3 py-2 text-right">
          <Button
            variant="secondary"
            size="sm"
            loading={syncing}
            onClick={onSyncNow}
          >
            Sync now
          </Button>
          <Button variant="ghost" size="sm" onClick={onToggleExpand}>
            {expanded ? "Hide" : "Details"}
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-line bg-surface/30">
          <td colSpan={8} className="px-4 py-4">
            <PlaylistDetail playlistId={p.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function PlaylistDetail({ playlistId }: { playlistId: string }) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["spotify", "playlists", playlistId, "albums"],
    queryFn: () => spotifyApi.playlists.albums(playlistId),
  });

  if (isLoading) {
    return <p className="text-xs text-foreground/60">Loading album matches…</p>;
  }
  if (isError) {
    return (
      <div className="text-xs text-red-300">
        Couldn't load album list:{" "}
        {error instanceof Error ? error.message : "unknown error"}{" "}
        <button
          onClick={() => refetch()}
          className="underline underline-offset-2"
        >
          retry
        </button>
      </div>
    );
  }

  const matched = data?.matched ?? [];
  const unmatched = data?.unmatched ?? [];

  if (matched.length === 0 && unmatched.length === 0) {
    return (
      <p className="text-xs text-foreground/60">
        No albums have been processed for this playlist yet.{" "}
        {isFetching && <span className="text-foreground/40">(refreshing…)</span>}
      </p>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground/60">
            Matched ({matched.length})
          </h3>
        </div>
        {matched.length === 0 ? (
          <p className="text-xs text-foreground/40">No matches yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {matched.map((m) => (
              <MatchedRow key={m.spotify_album_id} m={m} />
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground/60">
            Unmatched ({unmatched.length})
          </h3>
        </div>
        {unmatched.length === 0 ? (
          <p className="text-xs text-foreground/40">Everything matched.</p>
        ) : (
          <ul className="space-y-1.5">
            {unmatched.map((u) => (
              <UnmatchedRow key={u.spotify_album_id} u={u} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MatchedRow({ m }: { m: AlbumMatch }) {
  return (
    <li className="rounded border border-line bg-background/50 px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-foreground">
            {m.spotify_album_title}
          </div>
          <div className="truncate text-foreground/60">
            {m.spotify_album_artist}
          </div>
        </div>
        <span
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase",
            m.match_method === "upc"
              ? "bg-green-500/15 text-green-300"
              : "bg-blue-500/15 text-blue-300",
          )}
        >
          {m.match_method}
        </span>
      </div>
      <div className="mt-1 text-[10px] text-foreground/40">
        Queued {formatRelative(m.queued_at)}
      </div>
    </li>
  );
}

function UnmatchedRow({ u }: { u: AlbumMiss }) {
  return (
    <li className="rounded border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs">
      <div className="font-medium text-foreground">{u.spotify_album_title}</div>
      <div className="text-foreground/60">{u.spotify_album_artist}</div>
      <div className="mt-1 text-[10px] text-amber-300">{u.error}</div>
      <div className="mt-0.5 text-[10px] text-foreground/40">
        {u.upc ? `UPC ${u.upc} · ` : ""}
        {formatRelative(u.queued_at)}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "5m ago" / "2h ago" / "yesterday" / "Mar 12" — best-effort relative date. */
function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
